import { readDownloadResponse, type DownloadResponse } from './download/downloadResponse';
import { showMessage } from '@fuse/core/FuseMessage/fuseMessageSlice';
import apiService from '@/store/apiService';

export const addTagTypes = [
    'batches_courses',
    'batches_course',
    'batches_categories',
    'batches_datetime',
    'batches_recentWorkflows',
    'batches_available_count',
    'index_ocr',
    'BatchErrorCount',
    'HumanTaskActions',
    'WorkflowTaskAssignments'
] as const;

export interface AvailableHumanTaskAction {
    key: string;
    label: string;
    kind: 'approve' | 'deny' | 'return' | 'hold' | 'custom';
    requiresComment: boolean;
    confirmation?: string | null;
    targetStepId?: string | null;
    targetStepName?: string | null;
    assignment?: { type: 'user' | 'group' | 'unassigned'; id?: string | null } | null;
}

export interface AvailableHumanTaskActions {
    validationMessages?: string[];
    batchId: string;
    taskId: string;
    taskStatus: string;
    assignmentVersion: number;
    stepId: string;
    stepName: string;
    /** True even when every configured action was filtered by permission. */
    hasActionDefinitions: boolean;
    actions: AvailableHumanTaskAction[];
}

export interface WorkflowTaskAssignmentSnapshot {
    workflowTaskId: string;
    batchId: string;
    batchName: string;
    workflowStepId: string;
    stepName: string;
    stepType: string;
    taskStatus: string;
    assignmentState: string;
    assignmentVersion: number;
    assignee?: { id: string; email: string; displayName: string; photoUrl?: string | null } | null;
    candidateUsers: Array<{ id: string; email: string; displayName: string; photoUrl?: string | null }>;
    candidateGroups: Array<{ id: string; name: string }>;
    claimedAt?: string | null;
    assignmentUpdatedAt?: string | null;
    dueAt?: string | null;
    assignmentRuleId?: string | null;
    assignmentReason?: string | null;
    canClaim: boolean;
    canReturn: boolean;
    canManage: boolean;
}

// Types for batch datetime responses
export interface BatchDateTimeResponse {
    id: string;
    name: string;
    createdAt: string;
    completedAt?: string;
    modifiedAt?: string;
    status: string;
}

export interface BatchDateTimeSummary {
    totalBatches: number;
    completedBatches: number;
    pendingBatches: number;
    earliestBatch: string;
    latestBatch: string;
    lastModified?: string;
    averageProcessingTimeHours: number;
}

export interface BatchDateRangeRequest {
    startDate: string;
    endDate: string;
    dateType?: 'created' | 'completed' | 'modified';
}

// Available batches count interface
export interface AvailableBatchesCountResponse {
    count: number;
    waitingForSubmission: number;
    failed: number;
    processing: number;
}

export interface BatchErrorCountResponse {
    errorOccurredAt?: string | null;
    count: number;
    message: string;
    totalDocuments: number;
    errorDetails: string[];
    isBatchLevelError?: boolean;
    batchLevelErrorStep?: string | null;
}

// ✅ NEW: Consolidated batch errors interfaces
export interface BatchErrorDetail {
    errorOccurredAt?: string | null;
    id: string;
    batchId: string;
    batchName: string;
    status: string;
    workflowId?: string;
    workflowName: string;
    currentStep: string;
    stepType: string;
    totalDocuments: number;
    totalPages: number;
    errorCount: number;
    errorMessage: string;
    errorDetails: string[];
    isBatchLevelError?: boolean;
    createdAt: string;
    modifiedAt?: string;
    completedAt?: string;
}

export interface BatchErrorsResponse {
    data: BatchErrorDetail[];
    totalCount: number;
    page: number;
    pageSize: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
}

export interface BatchErrorsRequest {
    page?: number;
    pageSize?: number;
    search?: string;
    /** Exclude batches whose error was dismissed in the Errors console (used by the dashboard count). */
    excludeDismissed?: boolean;
}

export interface DocumentOCRResponse {
    ocrText: string;
}

export interface BatchHistoryItem {
    id: string;
    batchId: string;
    fromStepId: string;
    fromStep: string;
    fromStepType?: string;
    toStepId: string;
    toStep: string;
    toStepType?: string;
    transitionType: string;
    transitionedAt: string;
    completedAt?: string;
    transitionedBy?: string;
    userDisplayName?: string;
    notes?: string;
}

export interface BatchWithHistory {
    batchId: string;
    batchName: string;
    batchStatus: string;
    createdAt: string;
    completedAt?: string;
    history: BatchHistoryItem[];
}

export interface BatchHistoryPage {
    data: BatchWithHistory[];
    totalCount: number;
    page: number;
    pageSize: number;
    hasNextPage: boolean;
}

export interface BatchStepResponse {
    Step: {
        Id: string;
        Name: string;
        Type: string;
        Settings?: string;
        ParsedSettings?: any;
    };
    TemplateId?: string;
    DisplayName?: string;
}

export interface CaptureSubmissionStateResponse {
    isActive: boolean;
}

// Existing interfaces
export interface GetBatchesApiResponse {
    [key: string]: any;
}

export interface GetBatchesApiArg {
    courseId?: string;
    [key: string]: any;
}

export interface UpdateBatchApiResponse {
    [key: string]: any;
}

export interface UpdateBatchApiArg {
    courseId: string;
    data: any;
}

export interface GetWorkflowsCategoriesApiResponse {
    [key: string]: any;
}

export interface GetWorkflowsCategoriesApiArg {
    [key: string]: any;
}

const WorkflowsApi = apiService
    .enhanceEndpoints({
        addTagTypes
    })
    .injectEndpoints({
        endpoints: (build) => ({
            getIntakeBatches: build.query<IntakeBatch[], void>({
                query: () => ({ url: '/api/batches/intake' })
            }),
            getBatches: build.query<GetBatchesApiResponse, GetBatchesApiArg>({
                query: () => ({ url: `/api/batches` }),
                providesTags: ['batches_courses']
            }),
            getBatchDocuments: build.mutation<any, { batchId: string }>({
                query: (queryArg) => ({
                    url: `/api/capture/documents/${queryArg.batchId}`
                })
            }),
            getCaptureDocumentSummaries: build.mutation<
                { items: CaptureDocumentSummary[]; totalCount: number; offset: number; limit: number },
                { batchId: string; offset?: number; limit?: number }
            >({
                query: ({ batchId, offset = 0, limit = 500 }) => ({
                    url: `/api/capture/documents/${batchId}/summaries`,
                    params: { offset, limit }
                })
            }),
            getCaptureSubmissionState: build.mutation<CaptureSubmissionStateResponse, { batchId: string }>({
                query: ({ batchId }) => ({
                    url: `/api/capture/batches/${batchId}/submission-state`,
                    method: 'GET'
                })
            }),
            getBatchDocument: build.mutation<any, { batchId: string; order: number }>({
                query: (queryArg) => ({
                    url: `/api/capture/document/${queryArg.batchId}/${queryArg.order}`
                })
            }),
            getBatch: build.query<GetBatchesApiResponse, GetBatchesApiArg>({
                query: (queryArg) => ({
                    url: `/api/batches/${queryArg.courseId}`
                }),
                providesTags: ['batches_course']
            }),
            deleteCaptureDocuments: build.mutation<any, { batchId: string; documentIds: string[] }>({
                query: (queryArg) => ({
                    url: `/api/capture/document/delete`,
                    method: 'DELETE',
                    body: { BatchId: queryArg.batchId, DocumentIds: queryArg.documentIds }
                })
            }),
            deleteIndexDocuments: build.mutation<
                { success: boolean; deleted: number; cleanupPending: number; message: string },
                { batchId: string; documentIds: string[] }
            >({
                query: ({ batchId, documentIds }) => ({
                    url: `/api/index/document`,
                    method: 'DELETE',
                    body: { BatchId: batchId, DocumentIds: documentIds }
                }),
                invalidatesTags: ['batches_courses', 'batches_course', 'batches_datetime', 'batches_available_count']
            }),
            assembleIndexDocuments: build.mutation<any, any>({
                query: (body) => ({ url: '/api/index/review', method: 'POST', body }),
                invalidatesTags: ['batches_courses', 'batches_course', 'batches_datetime', 'batches_available_count']
            }),
            recoverIndexReview: build.mutation<any, { batchId: string }>({
                query: ({ batchId }) => ({ url: `/api/index/review/recover/${batchId}`, method: 'POST' })
            }),
            deleteIndexPages: build.mutation<
                { success: boolean; deletedPages: number; remainingPages: number; cleanupPending: number; message: string; code?: string },
                { batchId: string; documentId: string; pageNumbers: number[] }
            >({
                query: ({ batchId, documentId, pageNumbers }) => ({
                    url: `/api/index/delete-pages`,
                    method: 'POST',
                    body: { BatchId: batchId, DocumentId: documentId, PageNumbers: pageNumbers }
                }),
                invalidatesTags: ['batches_courses', 'batches_course', 'batches_datetime', 'batches_available_count']
            }),
            reorderCaptureDocuments: build.mutation<
                { count: number },
                { batchId: string; documents: Array<{ documentId: string; order: number }> }
            >({
                query: ({ batchId, documents }) => ({
                    url: `/api/capture/documents/${batchId}/order`,
                    method: 'PUT',
                    body: { Documents: documents }
                })
            }),
            uploadFormPdf: build.mutation({
                query: ({ batchId, formPdfBlob }) => {
                    const formData = new FormData();
                    formData.append('file', formPdfBlob, 'form.pdf');

                    return {
                        url: `/api/forms/${batchId}/upload`,
                        method: 'POST',
                        body: formData
                    };
                },
                async onQueryStarted(_, { dispatch, queryFulfilled }) {
                    try {
                        await queryFulfilled;
                        dispatch(showMessage({ message: 'Form uploaded successfully' }));
                    } catch (err) {
                        dispatch(showMessage({ message: 'Error uploading form' }));
                    }
                }
            }),
            downloadFormPdf: build.mutation({
                query: (batchId) => ({
                    url: `/api/forms/${batchId}/download`,
                    method: 'GET',
                    responseType: 'blob'
                })
            }),
            updateBatch: build.mutation<UpdateBatchApiResponse, UpdateBatchApiArg>({
                query: (queryArg) => ({
                    url: `/api/batches/${queryArg.courseId}`,
                    method: 'PUT',
                    body: queryArg.data
                }),
                async onQueryStarted(id, { dispatch, queryFulfilled }) {
                    try {
                        await queryFulfilled;
                        dispatch(showMessage({ message: 'Course Saved' }));
                    } catch (err) {
                        dispatch(showMessage({ message: 'Error Saving the course!' }));
                    }
                },
                invalidatesTags: ['batches_courses', 'batches_course', 'batches_datetime', 'batches_available_count']
            }),
            getStepFromBatch: build.mutation<any, { batchId: string }>({
                query: (queryArg) => ({
                    url: `/api/batches/${queryArg.batchId}/step`,
                    method: 'GET'
                })
            }),
            getWorkflowsRecent: build.query<GetWorkflowsCategoriesApiResponse, GetWorkflowsCategoriesApiArg>({
                query: () => ({ url: `/api/batches/recentWorkflows` }),
                providesTags: ['batches_recentWorkflows']
            }),
            submitBatch: build.mutation<unknown, BatchSubmitRequest>({
                query: ({ batchId, status, message }) => ({
                    url: `/api/batches/${batchId}`,
                    method: 'PUT',
                    body: { status, message }
                }),
                async onQueryStarted({ batchId, status }, { dispatch, queryFulfilled, getState }) {
                    // Optimistic update: immediately update the batch status in cache
                    const patchResult = dispatch(
                        WorkflowsApi.util.updateQueryData('getBatches', undefined, (draft) => {
                            const batch = draft?.find((b: any) => b.id === batchId);

                            if (batch) {
                                // Update status to Processing immediately for optimistic UI
                                batch.status = status === 'Submitted' ? 'Processing' : status;
                            }
                        })
                    );

                    try {
                        await queryFulfilled;
                        dispatch(showMessage({ message: 'Batch submitted successfully' }));
                        // Force a refetch of the available batches count
                        dispatch(WorkflowsApi.util.invalidateTags(['batches_available_count']));
                    } catch (err) {
                        // Revert optimistic update on error
                        patchResult.undo();
                        // dispatch(showMessage({ message: 'Error submitting batch' }));
                    }
                },
                invalidatesTags: ['batches_courses', 'batches_datetime', 'batches_available_count']
            }),
            createBatch: build.mutation<
                {
                    id: string;
                    name: string;
                    status: number;
                    workflowId: string;
                    createdAt: string;
                    stepType: string;
                },
                BatchCreateRequest
            >({
            query: ({ workflow, name, step, documentIds }) => ({
                url: '/api/batches/create',
                method: 'PUT',
                body: { workflow, step, name, documentIds }
            }),
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(showMessage({ message: 'Batch created successfully' }));

                    // Force immediate refetch of batch data by invalidating all related queries
                    dispatch(
                        WorkflowsApi.util.invalidateTags(['batches_courses', 'batches_datetime', 'batches_available_count'])
                    );
                } catch (err) {
                    dispatch(showMessage({ message: 'Error creating batch' }));
                }
            },
            invalidatesTags: ['batches_courses', 'batches_datetime', 'batches_available_count']
        }),
        updateDocumentStorage: build.mutation<void, { batchId: string; documentId: string; storageId: string }>({
            query: (queryArg) => ({
                url: `/api/capture/documents/${queryArg.batchId}/${queryArg.documentId}/storage`,
                method: 'PUT',
                body: { storageId: queryArg.storageId }
            })
        }),
        updateDocumentFields: build.mutation<void, { documentId: string; documentWithFields: DocumentWithFields }>({
            query: (arg) => {
                return {
                    url: `api/index/update/${arg.documentId}`,
                    method: 'PUT',
                    body: arg.documentWithFields
                };
            }
        }),
        // `silent` (move/complete/delete/cancel): bulk handlers set it so each call skips its
        // per-batch toast and per-batch tag invalidation — the bulk caller shows one summary
        // toast and invalidates once at the end instead of N times.
        moveBatch: build.mutation<void, { batchId: string; stepId: string; silent?: boolean }>({
            query: (queryArg) => ({
                url: `/api/batches/${queryArg.batchId}/move`,
                method: 'PUT',
                body: { stepId: queryArg.stepId }
            }),
            async onQueryStarted({ batchId, stepId, silent }, { dispatch, queryFulfilled }) {
                // Optimistic update: immediately update batch status and step
                const patchResult = dispatch(
                    WorkflowsApi.util.updateQueryData('getBatches', undefined, (draft) => {
                        const batch = draft?.find((b: any) => b.id === batchId);

                        if (batch && batch.task) {
                            // Store original values for rollback
                            const originalStepId = batch.task.workflowStepId;
                            const originalStatus = batch.status;

                            // Update to new step and set status to processing
                            batch.task.workflowStepId = stepId;
                            batch.status = 'Processing';
                            batch.modifiedAt = new Date().toISOString();

                            // Store rollback data
                            batch._rollbackData = { originalStepId, originalStatus };
                        }
                    })
                );

                try {
                    await queryFulfilled;
                    if (!silent) dispatch(showMessage({ message: 'Batch moved successfully' }));
                } catch (error) {
                    // Rollback optimistic update on error
                    patchResult.undo();
                    if (!silent) dispatch(showMessage({ message: 'Error moving batch' }));
                }
            },
            invalidatesTags: (result, error, arg) =>
                arg.silent ? [] : ['batches_courses', 'batches_datetime', 'batches_available_count']
        }),
        completeBatch: build.mutation<void, { batchId: string; silent?: boolean }>({
            query: (queryArg) => ({
                url: `/api/batches/${queryArg.batchId}/complete`,
                method: 'PUT'
            }),
            async onQueryStarted({ batchId, silent }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    WorkflowsApi.util.updateQueryData('getBatches', undefined, (draft) => {
                        const batch = draft?.find((b: any) => b.id === batchId);
                        if (batch) {
                            batch._rollbackData = { originalStatus: batch.status, originalCompletedAt: batch.completedAt };
                            batch.status = 'Completed';
                            batch.completedAt = new Date().toISOString();
                            batch.modifiedAt = new Date().toISOString();
                        }
                    })
                );
                try {
                    await queryFulfilled;
                    if (!silent) dispatch(showMessage({ message: 'Batch marked as completed' }));
                } catch (error) {
                    patchResult.undo();
                    if (!silent) dispatch(showMessage({ message: 'Error completing batch' }));
                }
            },
            invalidatesTags: (result, error, arg) =>
                arg.silent ? [] : ['batches_courses', 'batches_datetime', 'batches_available_count']
        }),
        deleteBatch: build.mutation<void, { batchId: string; silent?: boolean }>({
            query: (queryArg) => ({
                url: `/api/batches/${queryArg.batchId}/delete`,
                method: 'DELETE'
            }),
            async onQueryStarted({ batchId, silent }, { dispatch, queryFulfilled }) {
                // Optimistic update: immediately remove batch from cache
                const patchResult = dispatch(
                    WorkflowsApi.util.updateQueryData('getBatches', undefined, (draft) => {
                        const index = draft?.findIndex((batch: any) => batch.id === batchId);

                        if (index !== undefined && index >= 0) {
                            draft.splice(index, 1);
                        }
                    })
                );

                try {
                    await queryFulfilled;
                    if (!silent) dispatch(showMessage({ message: 'Batch deleted successfully' }));
                } catch (error) {
                    // Rollback optimistic update on error
                    patchResult.undo();
                    if (!silent) dispatch(showMessage({ message: 'Error deleting batch' }));
                }
            },
            invalidatesTags: (result, error, arg) =>
                arg.silent ? [] : ['batches_courses', 'batches_datetime', 'batches_available_count']
        }),
        // User-level batch locking.
        // claim: returns { claimed: true, heldByCurrentUser: true, lockedAt } on success,
        //        or 409 { message, heldByUserId, heldByDisplayName, lockedAt } when another
        //        user holds a non-stale lock. Stale (>2 min since heartbeat) can be taken over.
        claimBatchLock: build.mutation<any, { batchId: string }>({
            query: ({ batchId }) => ({
                url: `/api/batches/${batchId}/claim`,
                method: 'POST'
            })
        }),
        // release: clears the lock if the current user holds it; otherwise no-op. 200 either way.
        releaseBatchLock: build.mutation<any, { batchId: string; expectedLockedAt?: string }>({
            query: ({ batchId, expectedLockedAt }) => ({
                url: `/api/batches/${batchId}/release`,
                params: expectedLockedAt ? { expectedLockedAt } : undefined,
                method: 'POST'
            })
        }),
        // heartbeat: refreshes the lock's LockedAt so it doesn't go stale while the user is active.
        // Returns 409 if the lock was taken over (means someone else claimed via stale timeout).
        heartbeatBatchLock: build.mutation<any, { batchId: string }>({
            query: ({ batchId }) => ({
                url: `/api/batches/${batchId}/heartbeat`,
                method: 'POST'
            })
        }),
        // force-unlock: admin-only; clears the lock regardless of who holds it.
        // No invalidatesTags — caller should refetch getBatches explicitly, since the tag types
        // defined above don't include a plain "batches" entry.
        forceUnlockBatchLock: build.mutation<any, { batchId: string }>({
            query: ({ batchId }) => ({
                url: `/api/batches/${batchId}/force-unlock`,
                method: 'POST'
            })
        }),
        // On-demand download of a user-picked subset of a batch's documents from the
        // Download step inspection page. The saved step settings determine whether the
        // response is a ZIP or one raw output file; preserve the server's filename.
        downloadSelectedDocuments: build.mutation<DownloadResponse, {
            batchId: string;
            documentIds: string[];
        }>({
            query: ({ batchId, documentIds }) => ({
                url: `/api/batches/${batchId}/download-selected`,
                method: 'POST',
                body: { documentIds },
                responseHandler: readDownloadResponse
            })
        }),
        // Hard-delete a user-picked subset of a batch's documents at the Download step.
        // Removes batch residency while preserving live archives and shared source files.
        deleteSelectedDocuments: build.mutation<{ deleted: number; remaining: number }, {
            batchId: string;
            documentIds: string[];
        }>({
            query: ({ batchId, documentIds }) => ({
                url: `/api/batches/${batchId}/delete-documents`,
                method: 'POST',
                body: { documentIds }
            }),
            invalidatesTags: ['batches_courses']
        }),
        cancelBatch: build.mutation<void, { batchId: string; silent?: boolean }>({
            query: (queryArg) => ({
                url: `/api/batches/${queryArg.batchId}/cancel`,
                method: 'PUT'
            }),
            async onQueryStarted({ batchId, silent }, { dispatch, queryFulfilled }) {
                // Optimistic update: immediately update batch status to paused
                const patchResult = dispatch(
                    WorkflowsApi.util.updateQueryData('getBatches', undefined, (draft) => {
                        const batch = draft?.find((b: any) => b.id === batchId);

                        if (batch) {
                            // Store original status for rollback
                            const originalStatus = batch.status;

                            // Update status to paused
                            batch.status = 'WaitingForSubmission';
                            batch.modifiedAt = new Date().toISOString();

                            // Store rollback data
                            batch._rollbackData = { originalStatus };
                        }
                    })
                );

                try {
                    await queryFulfilled;
                    if (!silent) dispatch(showMessage({ message: 'Batch paused. Submit it when you’re ready to resume.' }));
                } catch (error) {
                    // Rollback optimistic update on error
                    patchResult.undo();
                    if (!silent) dispatch(showMessage({ message: 'Error cancelling batch' }));
                }
            },
            invalidatesTags: (result, error, arg) =>
                arg.silent ? [] : ['batches_courses', 'batches_datetime', 'batches_available_count']
        }),

        // Available batches count endpoint
        getAvailableBatchesCount: build.query<AvailableBatchesCountResponse, void>({
            query: () => ({ url: `/api/batches/available/count` }),
            providesTags: ['batches_available_count']
        }),

        // DateTime endpoints
        getBatchDateTime: build.query<BatchDateTimeResponse, { batchId: string }>({
            query: (queryArg) => ({
                url: `/api/batches/${queryArg.batchId}/datetime`
            }),
            providesTags: (result, error, arg) => [{ type: 'batches_datetime', id: arg.batchId }]
        }),

        getBatchesDateTime: build.mutation<BatchDateTimeResponse[], { batchIds: string[] }>({
            query: (queryArg) => ({
                url: '/api/batches/datetime/bulk',
                method: 'POST',
                body: queryArg.batchIds
            })
        }),

        getBatchesByDateRange: build.query<BatchDateTimeResponse[], BatchDateRangeRequest>({
            query: (queryArg) => ({
                url: '/api/batches/datetime/range',
                params: {
                    startDate: queryArg.startDate,
                    endDate: queryArg.endDate,
                    dateType: queryArg.dateType || 'created'
                }
            }),
            providesTags: ['batches_datetime']
        }),

        getBatchDateTimeSummary: build.query<BatchDateTimeSummary, void>({
            query: () => ({
                url: '/api/batches/datetime/summary'
            }),
            providesTags: ['batches_datetime']
        }),

        // FlatFile step output for a batch (newest first). Empty when the batch has no exports.
        getBatchExports: build.query<BatchExport[], { batchId: string }>({
            query: (queryArg) => ({
                url: `/api/batches/${queryArg.batchId}/exports`
            }),
            keepUnusedDataFor: 0
        }),

        downloadBatchExport: build.mutation<Blob, { batchId: string; exportId: string }>({
            query: (queryArg) => ({
                url: `/api/batches/${queryArg.batchId}/exports/${queryArg.exportId}/download`,
                method: 'GET',
                // JSON error bodies stay readable so the button can show the server's message.
                responseHandler: (response) => (response.ok ? response.blob() : response.json()),
                cache: 'no-cache'
            })
        }),

        // Per-batch error count endpoint (legacy - kept for backward compatibility)
        getBatchErrorCount: build.query<BatchErrorCountResponse, { batchId: string }>({
            query: (queryArg) => ({
                url: `/api/batches/${queryArg.batchId}/errors/count`
            }),
            providesTags: (result, error, arg) => [{ type: 'BatchErrorCount', id: arg.batchId }, 'BatchErrorCount']
        }),

        // ✅ NEW: Consolidated batch errors endpoint (optimized)
         getBatchesWithErrors: build.query<BatchErrorsResponse, BatchErrorsRequest | void>({
                query: (queryArg) => {
                    // ✅ FIX: Safely handle void/undefined with explicit default
                    const params = new URLSearchParams();
                    const options: BatchErrorsRequest = queryArg || {};
                    const page = options.page;
                    const pageSize = options.pageSize;
                    const search = options.search;
                    const excludeDismissed = options.excludeDismissed;

                    if (page) params.append('page', page.toString());
                    if (pageSize) params.append('pageSize', pageSize.toString());
                    if (search) params.append('search', search);
                    if (excludeDismissed) params.append('excludeDismissed', 'true');

                    return {
                        url: `/api/batches/errors${params.toString() ? `?${params.toString()}` : ''}`
                    };
                },
                providesTags: ['BatchErrorCount'],
                keepUnusedDataFor: 30
        }),
          
        getDocumentOCR: build.query<DocumentOCRResponse, { documentId: string }>({
            query: (queryArg) => ({
                url: `/api/batches/document/${queryArg.documentId}/ocr`
            }),
            providesTags: (result, error, arg) => [{ type: 'index_ocr', id: arg.documentId }]
        }),

        getBatchStep: build.query<BatchStepResponse, { batchId: string }>({
            query: (queryArg) => ({
                url: `/api/batches/${queryArg.batchId}/step`,
                method: 'GET'
            }),
            providesTags: (result, error, arg) => [{ type: 'batches_course', id: arg.batchId }]
        }),

        getBatchHistory: build.query<any[], { batchId: string }>({
            query: (queryArg) => ({
                url: `/api/batches/${queryArg.batchId}/history`
            }),
            providesTags: (result, error, arg) => [{ type: 'batches_course', id: arg.batchId }]
        }),

        performRegionOCR: build.mutation<{ text: string }, {
            batchId: string;
            documentOrder: string;
            pageNumber: number;
            zone: string;
        }>({
            query: (arg) => ({
                url: `/api/index/ocr/region`,
                method: 'POST',
                body: arg
            })
        }),

        getAllBatchHistories: build.query<BatchHistoryPage, { page?: number; pageSize?: number; search?: string } | void>({
            query: (params) => ({ url: '/api/batches/all-histories', params: params || {} }),
            providesTags: ['batches_courses']
        }),

        getAvailableHumanTaskActions: build.query<AvailableHumanTaskActions, { batchId: string }>({
            query: ({ batchId }) => ({ url: `/api/batches/${batchId}/actions` }),
            providesTags: (_result, _error, arg) => [{ type: 'HumanTaskActions', id: arg.batchId }]
        }),

        executeHumanTaskAction: build.mutation<any, {
            batchId: string;
            taskId: string;
            actionKey: string;
            comment?: string;
            idempotencyKey: string;
            expectedTaskStatus: string;
            expectedAssignmentVersion: number;
        }>({
            query: ({ batchId, ...body }) => ({
                url: `/api/batches/${batchId}/actions`,
                method: 'POST',
                body
            }),
            invalidatesTags: (_result, _error, arg) => [
                { type: 'HumanTaskActions', id: arg.batchId },
                'WorkflowTaskAssignments',
                'batches_courses'
            ]
        }),

        getWorkflowTaskAssignments: build.query<WorkflowTaskAssignmentSnapshot[], {
            queue?: 'myWork' | 'assignedToMe' | 'available' | 'unassigned' | 'all';
            batchId?: string;
            take?: number;
        }>({
            query: (params = {}) => ({ url: '/api/workflow-task-assignments', params }),
            providesTags: ['WorkflowTaskAssignments']
        }),

        claimWorkflowTask: build.mutation<WorkflowTaskAssignmentSnapshot, { workflowTaskId: string; expectedVersion: number }>({
            query: ({ workflowTaskId, expectedVersion }) => ({
                url: `/api/workflow-task-assignments/${workflowTaskId}/claim`,
                method: 'POST',
                body: { expectedVersion }
            }),
            invalidatesTags: ['WorkflowTaskAssignments', 'HumanTaskActions', 'batches_courses']
        }),

        // Explicit reassignment, including a manager taking work over. The API requires the
        // batch-management permission and bumps assignmentVersion, so pass the version seen.
        replaceWorkflowTaskAssignment: build.mutation<WorkflowTaskAssignmentSnapshot, {
            workflowTaskId: string;
            expectedVersion: number;
            assigneeUserId?: string | null;
            candidateUserIds?: string[];
            candidateGroupIds?: string[];
            dueAt?: string | null;
            reason?: string;
        }>({
            query: ({ workflowTaskId, ...body }) => ({
                url: `/api/workflow-task-assignments/${workflowTaskId}`,
                method: 'PUT',
                body
            }),
            invalidatesTags: ['WorkflowTaskAssignments', 'HumanTaskActions', 'batches_courses']
        }),

        returnWorkflowTask: build.mutation<WorkflowTaskAssignmentSnapshot, {
            workflowTaskId: string;
            expectedVersion: number;
            reason?: string;
        }>({
            query: ({ workflowTaskId, ...body }) => ({
                url: `/api/workflow-task-assignments/${workflowTaskId}/return`,
                method: 'POST',
                body
            }),
            invalidatesTags: ['WorkflowTaskAssignments', 'HumanTaskActions', 'batches_courses']
        })
    })
  });

export default WorkflowsApi;

export type BatchSubmitRequest = {
    batchId: string;
    status: string;
    message?: string;
};

export type CaptureDocumentSummary = {
    downloadOnly?: boolean;
    originalFileName?: string;
    id: string;
    name: string;
    batchId: string;
    order: number;
    pageCount: number;
    storageId?: string | null;
    updatedAt?: string;
};

interface BatchCreateRequest {
    workflow: string;
    step: string;
    name: string;
    documentIds?: string[];
}

interface DocumentWithFields {
    id: string;
    batchId: string;
    batchPath: string;
    archivePath: string;
    contentType?: string;
    status?: string;
    createdAt: string;
    totalPages: number;
    errorDetails?: string;
    processingNotes?: string;
    fileSize: number;
    storageId: string;
    order: number;
    IndexSets: Array<{
        id: string;
        field: string;
        value?: string;
        type: string;
        order: number;
    }>;
}

export type BatchExport = {
    id: string;
    fileName: string;
    documentCount: number;
    pageCount: number;
    status: string;
    createdAt: string;
    /** False when the recorded file is no longer on the server. */
    available: boolean;
};

export type Category = {
    id: string;
    workflow: string;
    step: string;
    name: string;
};

export const {
    useGetIntakeBatchesQuery,
    useGetBatchesQuery,
    useGetBatchDocumentsMutation,
    useGetCaptureDocumentSummariesMutation,
    useGetCaptureSubmissionStateMutation,
    useGetBatchDocumentMutation,
    useUpdateBatchMutation,
    useGetWorkflowsRecentQuery,
    useSubmitBatchMutation,
    useCreateBatchMutation,
    useUpdateDocumentStorageMutation,
    useUpdateDocumentFieldsMutation,
    useMoveBatchMutation,
    useDeleteBatchMutation,
    useDownloadSelectedDocumentsMutation,
    useDeleteSelectedDocumentsMutation,
    useCancelBatchMutation,
    useCompleteBatchMutation,
    // Batch locking
    useClaimBatchLockMutation,
    useReleaseBatchLockMutation,
    useHeartbeatBatchLockMutation,
    useForceUnlockBatchLockMutation,
    useDeleteCaptureDocumentsMutation,
    useDeleteIndexDocumentsMutation,
    useDeleteIndexPagesMutation,
    useAssembleIndexDocumentsMutation,
    useRecoverIndexReviewMutation,
    useReorderCaptureDocumentsMutation,
    useUploadFormPdfMutation,
    useDownloadFormPdfMutation,
    useGetStepFromBatchMutation,
    // Error count Hook
    useGetBatchErrorCountQuery,
    useGetBatchExportsQuery,
    useDownloadBatchExportMutation,
    useGetBatchesWithErrorsQuery,
    // Available batches count hook
    useGetAvailableBatchesCountQuery,
    // DateTime hooks
    useGetBatchDateTimeQuery,
    useGetBatchesDateTimeMutation,
    useGetBatchesByDateRangeQuery,
    useGetBatchDateTimeSummaryQuery,
    // OCR hooks
    useGetDocumentOCRQuery, // Add this new hook
    useGetBatchHistoryQuery,
    useGetAllBatchHistoriesQuery,
    useGetBatchStepQuery,
    usePerformRegionOCRMutation,
    useGetAvailableHumanTaskActionsQuery,
    useExecuteHumanTaskActionMutation,
    useGetWorkflowTaskAssignmentsQuery,
    useClaimWorkflowTaskMutation,
    useReplaceWorkflowTaskAssignmentMutation,
    useReturnWorkflowTaskMutation
} = WorkflowsApi;

export interface IntakeBatch {
    id: string;
    name: string;
    createdAt: string;
    deviceName: string;
    workflowId: string | null;
    workflowName: string;
    progress: {
        totalDocuments: number; waiting: number; prepared: number; routed: number; failed: number;
        totalPages: number | null; stage: string;
    };
}
